package com.zi.support.web

import com.zi.support.shared.DirectoryCache
import com.zi.support.shared.DirectoryField
import com.zi.support.shared.DirectoryRow
import com.zi.support.shared.SettingsDirectories
import com.zi.support.shared.SettingsDirectory
import kotlinx.browser.document
import org.w3c.dom.HTMLElement

// Settings: support users, customers (emails, phones, companies), allowed domains and emails, one filter
// button each (SettingsDirectories in shared). Everything goes through the manageDirectory function:
// any support user can look, admins can add, edit and delete. DirectoryCache keeps what's loaded, so
// reopening a tab doesn't re-read it; email lists only fetch what's newer.
internal object SettingsDirectoryView {
    private var tabIndex = 0
    private var directoryIndex = 0
    private var rows: List<DirectoryRow> = emptyList()
    private var canEdit = false
    private var search = ""
    private var page = 0
    private const val PAGE_SIZE = 30

    private val directory: SettingsDirectory get() = SettingsDirectories.tabs[tabIndex].directories[directoryIndex]

    fun render(containerId: String) {
        val root = element(containerId) ?: return
        root.innerHTML = """
          <div class="sx-dir-head">
            <div class="sx-tabs" id="settings-tabs" role="tablist">${SettingsDirectories.tabs.mapIndexed { i, tab -> "<button data-settings-tab=\"$i\"${if (i == tabIndex) " class=\"active\"" else ""}>${escape(tab.label)}</button>" }.joinToString("")}</div>
          </div>
          <div class="sx-subtabs" id="settings-subtabs"></div>
          <div class="sx-dir-tools">
            <div class="search-shell"><span class="material-symbols-rounded" aria-hidden="true">search</span><label class="visually-hidden" for="settings-search">Search</label><input id="settings-search" class="input search" placeholder="Search…" value="${escape(search)}"></div>
            <button id="settings-refresh" class="btn btn-icon" title="Refresh" aria-label="Refresh"><span class="material-symbols-rounded">refresh</span></button>
            <button id="settings-add" class="btn btn-primary" hidden><span class="material-symbols-rounded">add</span> Add</button>
          </div>
          <div id="settings-rows" class="sx-rows"><div class="empty">Loading…</div></div>
        """.trimIndent()
        bindAll("[data-settings-tab]") { node -> tabIndex = node.getAttribute("data-settings-tab")!!.toInt(); directoryIndex = 0; page = 0; render(containerId) }
        // The box always shows the search in use (it survives tab switches), and clearing it shows everything again.
        element("settings-search")?.addEventListener("input", { search = input("settings-search").lowercase(); page = 0; paint() })
        bindSearchClear("settings-search") { search = ""; page = 0; paint() }
        click("settings-add") { edit(null) }
        click("settings-refresh") { DirectoryCache.forget(directory); show(); load() }
        val subtabs = SettingsDirectories.tabs[tabIndex].directories
        element("settings-subtabs")?.let { node ->
            node.hidden = subtabs.size < 2
            node.innerHTML = subtabs.mapIndexed { i, d -> "<button data-settings-sub=\"$i\"${if (i == directoryIndex) " class=\"active\"" else ""}>${escape(d.label)}</button>" }.joinToString("")
        }
        bindAll("[data-settings-sub]") { node -> directoryIndex = node.getAttribute("data-settings-sub")!!.toInt(); page = 0; render(containerId) }
        if (DirectoryCache.isLoaded(directory)) show()
        if (DirectoryCache.needsLoad(directory)) load()
    }

    /** Paints whatever DirectoryCache holds for the open list. */
    private fun show() {
        rows = DirectoryCache.rows(directory)
        canEdit = DirectoryCache.canEdit(directory)
        element("settings-add")?.hidden = !(canEdit && directory.canAdd)
        if (DirectoryCache.isLoaded(directory)) paint()
    }

    private fun bindAll(selector: String, onClick: (HTMLElement) -> Unit) {
        val nodes = document.querySelectorAll(selector)
        for (i in 0 until nodes.length) { val node = nodes.item(i) as HTMLElement; node.addEventListener("click", { onClick(node) }) }
    }

    /** First load, or (email lists) only what's newer than the rows already shown. */
    private fun load() {
        val requested = directory
        val payload = json("directory" to requested.id, "action" to "list")
        DirectoryCache.request(requested).forEach { (key, value) -> payload[key] = value }
        promise(FirebaseGateway.callable("manageDirectory", payload), { result ->
            val list = result.data.rows
            val loaded = (0 until (list.length as Int)).map { i ->
                val v = list[i].values
                DirectoryRow(list[i].id as String, (js("Object.keys")(v) as Array<String>).associateWith { key -> v[key] as String })
            }
            DirectoryCache.store(requested, loaded, result.data.canEdit == true, result.data.isAdmin == true)
            if (requested.id == directory.id) show()  // another tab may have been opened meanwhile
        }, { message -> element("settings-rows")?.innerHTML = "<div class=\"empty\">${escape(message)}</div>" })
    }

    /** After an add, edit or delete: reload that list. */
    private fun reload() { DirectoryCache.forget(directory); load() }

    private fun paint() {
        val list = element("settings-rows") ?: return
        val shown = rows.filter { search.isBlank() || (listOf(it.id) + it.values.values).any { v -> v.lowercase().contains(search) } }
        if (shown.isEmpty()) { list.innerHTML = "<div class=\"empty\">${escape(if (rows.isEmpty()) directory.emptyText else "No matches.")}</div>"; return }
        val pages = (shown.size + PAGE_SIZE - 1) / PAGE_SIZE
        page = page.coerceIn(0, pages - 1)
        val first = page * PAGE_SIZE
        val pageRows = shown.drop(first).take(PAGE_SIZE)
        val pager = if (pages < 2) "" else "<div class=\"calls-pager\"><button type=\"button\" class=\"btn\" data-dir-page=\"${page - 1}\"${if (page == 0) " disabled" else ""}>‹ Previous</button>" +
            "<span>${first + 1}–${first + pageRows.size} of ${shown.size}</span><button type=\"button\" class=\"btn\" data-dir-page=\"${page + 1}\"${if (page >= pages - 1) " disabled" else ""}>Next ›</button></div>"
        list.innerHTML = "<div class=\"sx-list\">" + pageRows.mapIndexed { i, row ->
            val actions = if (!canEdit) "" else buildString {
                if (directory.canInvite) append("<button class=\"sx-act\" id=\"settings-invite-$i\" title=\"Invite\"><span class=\"material-symbols-rounded\" aria-hidden=\"true\">forward_to_inbox</span><span>Invite</span></button>")
                if (directory.canChangePassword) append("<button class=\"sx-act\" id=\"settings-password-$i\" title=\"Change password\"><span class=\"material-symbols-rounded\" aria-hidden=\"true\">key</span><span>Password</span></button>")
                if (directory.fields.isNotEmpty()) append("<button class=\"sx-act sx-act-icon\" id=\"settings-edit-$i\" title=\"Edit\" aria-label=\"Edit\"><span class=\"material-symbols-rounded\" aria-hidden=\"true\">edit</span></button>")
                if (directory.canDelete) append("<button class=\"sx-act sx-act-icon sx-act-danger\" id=\"settings-delete-$i\" title=\"Delete\" aria-label=\"Delete\"><span class=\"material-symbols-rounded\" aria-hidden=\"true\">delete</span></button>")
            }
            val title = SettingsDirectories.title(directory, row.id, row.values)
            """<div class="sx-row">${avatarHtml(title)}<div class="sx-row-text"><strong>${escape(title)}</strong><small>${escape(SettingsDirectories.subtitle(directory, row.values))}</small></div><div class="sx-row-actions">$actions</div></div>"""
        }.joinToString("") + "</div>" + pager
        bindAll("[data-dir-page]") { node -> page = node.getAttribute("data-dir-page")!!.toInt(); paint(); element("settings-search")?.asDynamic()?.scrollIntoView(js("({block:'center'})")) }
        pageRows.forEachIndexed { i, row ->
            click("settings-password-$i") { changePassword(row) }
            click("settings-invite-$i") { invite(row) }
            click("settings-edit-$i") { edit(row) }
            click("settings-delete-$i") { delete(row) }
        }
    }

    /** Add (row == null) or edit a row; the ID field is only asked when adding. */
    private fun edit(row: DirectoryRow?) {
        val d = directory
        val form = d.formFields(adding = row == null)
        fun fieldHtml(f: DirectoryField, value: String) =
            if (f.choices.isNotEmpty()) "<div class=\"field\"><label for=\"dir-${f.key}\">${escape(f.label)}</label><select id=\"dir-${f.key}\">${f.choices.joinToString("") { "<option${if (it == value) " selected" else ""}>${escape(it)}</option>" }}</select></div>"
            else "<div class=\"field\"><label for=\"dir-${f.key}\">${escape(f.label)}</label><input id=\"dir-${f.key}\" class=\"input\"${if (f.secret) " type=\"password\" autocomplete=\"new-password\"" else ""} value=\"${escape(value)}\"></div>"
        val body = form.joinToString("") { fieldHtml(it, row?.values?.get(it.key).orEmpty()) }
        modal(if (row == null) "Add to ${d.label.lowercase()}" else SettingsDirectories.title(d, row.id, row.values), body,
            "<button id=\"dir-save\" class=\"btn btn-primary\">${if (row == null) "Add" else "Save"}</button>")
        click("dir-save") {
            val values = json()
            form.forEach { f -> values[f.key] = if (f.choices.isNotEmpty()) select("dir-${f.key}") else input("dir-${f.key}") }
            promise(FirebaseGateway.callable("manageDirectory", json("directory" to d.id, "action" to "save", "create" to (row == null), "id" to row?.id, "values" to values)), {
                closeModal(); toast(if (row == null) "Added." else "Saved."); reload()
            }, { message -> toast(message, true) })
        }
    }

    private fun delete(row: DirectoryRow) {
        val d = directory
        confirmModal("Delete ${SettingsDirectories.title(d, row.id, row.values)}?", "This removes it from ${d.label.lowercase()} and can't be undone.", "dir-delete", "Delete")
        click("dir-delete") {
            promise(FirebaseGateway.callable("manageDirectory", json("directory" to d.id, "action" to "delete", "id" to row.id)), {
                closeModal(); toast("Deleted."); reload()
            }, { message -> toast(message, true) })
        }
    }

    private fun invite(row: DirectoryRow) {
        val d = directory
        promise(FirebaseGateway.callable("manageDirectory", json("directory" to d.id, "action" to "invite", "id" to row.id)), {
            toast("Invite sent to ${SettingsDirectories.title(d, row.id, row.values)}."); reload()
        }, { message -> toast(message, true) })
    }

    private fun changePassword(row: DirectoryRow) {
        modal("New password for ${row.values["username"] ?: row.id}", """
          <div class="field"><label for="dir-password">New password</label><input id="dir-password" class="input" type="password" minlength="6" autocomplete="new-password"></div>
          <div class="field"><label for="dir-password-confirm">Confirm new password</label><input id="dir-password-confirm" class="input" type="password" minlength="6" autocomplete="new-password"></div>
          <p class="field-help">They'll get a notification on their phone with the new password.</p>
        """.trimIndent(), "<button id=\"dir-password-save\" class=\"btn btn-primary\">Change password</button>")
        click("dir-password-save") {
            val password = input("dir-password")
            if (password.length < 6) { toast("Password must contain at least 6 characters.", true); return@click }
            if (password != input("dir-password-confirm")) { toast("The passwords do not match.", true); return@click }
            promise(FirebaseGateway.callable("changeSupportUserPassword", json("uid" to row.values["uid"], "password" to password)), {
                toast("Password updated."); closeModal()
            }, { message -> toast(message, true) })
        }
    }
}
